
import { Options, Vue, prop } from "vue-class-component";
import SelectPhoneCountry from "../../../shipment/booking/component/select-phone-country.vue";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { OptionsClass } from "@/domain/entities/MainApp";
import { debounce } from "lodash";
import Geolocation from "../geolocation/index.vue";
import { ValidateInputPhoneNumber } from "@/app/infrastructures/misc/common-library/ValidateInputPhoneNumber";
import { PickupScheduleController } from "@/app/ui/controllers/PickupScheduleController";
import { PickupCorporateAddressRequest } from "@/data/payload/api/PickupScheduleApiRequest";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { PropType } from "vue";
import { codePhoneCountry } from "@/app/ui/views/shipment/booking/booking-utils";
import {
  AddressFormSuccessPayload,
  AddressFormType
} from "../saved-address-list/types";

class Props {
  formType = prop<AddressFormType>({
    required: true
  });
  clientId = prop<number>({
    default: 0,
    required: false
  });
  idAddress = prop<number>({
    default: 0,
    required: false
  });
  onClose = prop<any>({
    default: Function as PropType<() => void>,
    type: Function
  });
  modelValue = prop<any>({
    default: "",
    required: false
  });
  forceDefault = prop<boolean>({
    default: false,
    required: false
  });
  isVisible = prop<boolean>({
    default: false,
    required: false
  });
}

@Options({
  emits: ["onSuccess"],
  components: {
    SelectPhoneCountry,
    Geolocation
  },
  watch: {
    modelValue: function() {
      this.getAllData();
    },
    forceDefault: {
      immediate: true,
      handler(newVal: boolean) {
        if (newVal && this.formType === "add") {
          this.isDefault = true;
        }
      }
    },
    isVisible: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.getIsDefault();
        } else {
          this.isGeolocationOpen = false;
          PickupScheduleController.setSuccessAddAddress(false);
          this.clearForm();
        }
      }
    }
  }
})
export default class ModalAddNewAddress extends Vue.with(Props) {
  locationController = LocationController;
  addressName = "";
  picName = "";
  districtId: any = {};
  geoloc = "";
  postalCode = "";
  isDefault = false;
  isGeolocationOpen = false;

  onGeolocationVisibilityChange(isVisible: boolean) {
    this.isGeolocationOpen = isVisible;
  }

  getAllData() {
    let phoneNumberCode = "+62";
    if (this.modelValue.pcaPhoneNumber) {
      phoneNumberCode = codePhoneCountry(this.modelValue.pcaPhoneNumber).code;
    } else {
      phoneNumberCode =
        AccountController.accountData?.countryCode.toUpperCase() === "ID"
          ? "+62"
          : "+60";
    }
    this.addressName = this.modelValue.pcaName;
    this.picName = this.modelValue.pcaPicName;
    this.address = this.modelValue.pcaAddress;
    this.postalCode = this.modelValue.pcaPostalCode;
    this.geoloc = this.modelValue.pcaLatlon;
    this.phoneNumber = codePhoneCountry(this.modelValue.pcaPhoneNumber).number;
    this.phoneNumberCode = phoneNumberCode;
    this.districtId = this.modelValue.pcaDistrict;
    this.isDefault = this.modelValue.pcaIsDefault;
  }

  getIsDefault() {
    if (this.formType === "add" && this.forceDefault) {
      this.isDefault = this.forceDefault;
    }
  }

  get isCheckboxDefaultDisabled(): boolean {
    // Disable if mode 'add' and forceDefault true (add new address)
    return this.formType === "add" && this.forceDefault;
  }

  get accountData() {
    return AccountController.accountData;
  }

  fetchDistricts(search: string, cityCode = "", limit = 10) {
    return new Promise((resolve) => {
      this.locationController
        .getDistrictList({
          page: 1,
          limit: limit,
          search,
          status: "active",
          cache: true,
          cityCode: cityCode,
          excludeCountry: "",
          type: ""
        })
        .then((res) => resolve(res));
    });
  }

  get modalTitle() {
    if (this.formType === "edit") {
      return this.$t("pickupAddress.form.editTitle");
    } else {
      return this.$t("pickupAddress.form.addTitle");
    }
  }

  openGoogleMaps() {
    const query = this.geoloc.trim();
    const url = query
      ? `https://www.google.com/maps?q=${encodeURIComponent(query)}`
      : "https://www.google.com/maps";
    window.open(url, "_blank", "noopener,noreferrer");
  }

  // phone number
  phoneNumber = "";
  phoneNumberCode =
    AccountController.accountData?.countryCode.toUpperCase() === "ID"
      ? "+62"
      : "+60";
  validPhoneNumber = true;

  phoneNumberError = {
    success: false,
    error: false,
    message: ""
  };

  error = {
    phoneNumber: false,
    phoneNumberCode: false
  };

  setPhoneNumber(val: string) {
    let selectedPhoneCode = "";
    if (this.phoneNumberCode !== null && this.phoneNumberCode !== "") {
      selectedPhoneCode = this.phoneNumberCode.substring(1);
    }
    const phoneNumberWithCode = `${selectedPhoneCode}${val}`;
    this.phoneNumberError = ValidateInputPhoneNumber(phoneNumberWithCode);
    this.setErrorPhoneNumberCode(this.phoneNumberError.error);
    this.phoneNumber = val;
  }

  setPhoneCodeNumber(val: string) {
    this.phoneNumberCode = val;
  }

  setErrorPhoneNumberCode(status: boolean) {
    this.error.phoneNumberCode = status;
  }

  // district
  get districtOptions() {
    return this.locationController.districtData.districtData.map((item) => {
      return new OptionsClass({
        name: item.districtNameCityName,
        id: item.id
      });
    });
  }
  onFocusDistricts() {
    this.fetchDistricts("");
  }

  onSearchDistricts = debounce((search: string) => {
    this.fetchDistricts(search);
  }, 500);

  onSelectDistrict(item: OptionsClass): void {
    const district = this.locationController.districtData.districtData.find(
      (district) => district.id === Number(item.id)
    );

    this.postalCode = district?.originZipCode || "";
  }

  // address
  address = "";

  get disableButtonSave() {
    return !this.isFormValid || this.phoneNumberError.error;
  }

  get isFormValid() {
    return (
      this.addressName &&
      this.picName &&
      this.phoneNumber &&
      this.address &&
      this.districtId.id &&
      this.geoloc
    );
  }

  clearForm() {
    this.addressName = "";
    this.picName = "";
    this.address = "";
    this.phoneNumber = "";
    this.phoneNumberCode =
      AccountController.accountData?.countryCode.toUpperCase() === "ID"
        ? "+62"
        : "+60";
    this.districtId = "";
    this.postalCode = "";
    this.geoloc = "";
    this.isDefault = this.formType === "add" ? this.forceDefault : false;
    this.phoneNumberError.error = false;
    this.phoneNumberError.success = false;
    this.phoneNumberError.message = "";
  }

  saveAddress() {
    const payload = new PickupCorporateAddressRequest({
      pcaAddress: this.address,
      pcaClientId: this.accountData.isInternalAccount ? this.clientId : 0,
      pcaDistrictId: Number(this.districtId.id),
      pcaIsDefault: this.isDefault,
      pcaLatlon: this.geoloc.replace(/\s+/g, ""),
      pcaName: this.addressName,
      pcaPhoneNumber: `${this.phoneNumberCode.substring(1)}${this.phoneNumber}`,
      pcaPicName: this.picName,
      pcaPostalCode: this.postalCode
    });

    if (this.formType === "add") {
      PickupScheduleController.addPickupCorporateAddress(payload);
    } else {
      PickupScheduleController.updatePickupCorporateAddress({
        data: payload,
        id: this.idAddress
      });
    }
  }

  get successTitle() {
    if (this.formType === "add") {
      return this.$t("pickupAddress.success.addedTitle", {
        name: this.addressName
      });
    } else {
      return this.$t("pickupAddress.success.editedTitle", {
        name: this.addressName
      });
    }
  }

  get openSuccess() {
    return PickupScheduleController.isSuccessAddAddress;
  }

  onSuccessAddAddress() {
    const payload: AddressFormSuccessPayload = {
      isDefault: this.isDefault
    };
    PickupScheduleController.setSuccessAddAddress(false);
    this.$emit("onSuccess", payload);
    this.clearForm();
  }
}
